import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MuseumWalkthrough, { RoundBadge } from "./MuseumWalkthrough";
import type { EventItem, Room, RoomItem, Work, WorkGroup } from "./MuseumWalkthrough";

// "Proof of Culture" — the Culture Line. See claude project doc
// "Proof_of_Culture_Exhibition_Concept_Notes.md" for the confirmed prototype specs this
// ports. Flow, compressed twice after Sian's feedback (first: too many separate "THE
// BEGINNING" screens; then: still one screen too many): timeline -> one threshold page
// where the curatorial copy fades in paragraph by paragraph, then a "scroll to begin" cue
// -> scrolling (or a swipe/arrow-key) dissolves straight into the works corridor. No
// separate title/button page anymore -- the section title still appears twice, once on
// the timeline node and once on the corridor's own room-title slide, which is enough.
//
// Still fully static -- reads local files under public/media/proof-of-culture/<year>/,
// never a live OpenSea call.

type Milestone = {
  id: string;
  year: string;
  title: string;
  narrative: string;
  posPct: number;
  shape: "circle" | "diamond";
  color: string;
  ready: boolean;
  // Most milestones are thresholds into a themed room in the corridor. Digital Diaspora (the
  // June 2021 exhibition) is a single event worth marking on the line without building a whole
  // section around it -- clicking it opens a small popup with a short blurb instead of routing
  // into the copy/corridor flow at all. kind defaults to a room when omitted. "cluster" is a
  // group of several close-together info events sharing one slot on the line -- see
  // clusterMembers and Sian's "constellation" note, Sept 15.
  kind?: "room" | "info" | "cluster";
  blurb?: string[];
  // Optional image shown above the blurb text in MilestoneInfoPopup -- e.g. a photo from the
  // Digital Diaspora exhibition itself. Path relative to /public, same convention as a
  // section's own work images.
  blurbImage?: string;
  // Short label shown on the timeline node itself, when the real title (used verbatim in the
  // popup) is too long to sit comfortably in the node's fixed-width label -- e.g. "IX Shells"
  // for "IX Shells Sets $2 Million Sales Record". Falls back to the full title when omitted.
  shortTitle?: string;
  // Only set when kind === "cluster": several individually-clickable events sharing one
  // timeline slot instead of each taking up its own -- rendered as a tight little group of
  // dots (a literal "constellation") rather than one plain node. Each member opens its own
  // MilestoneInfoPopup exactly like a normal info milestone would.
  clusterMembers?: ClusterMember[];
};

type ClusterMember = {
  id: string;
  year: string;
  title: string;
  blurb: string[];
  blurbImage?: string;
};

// Builds a full Milestone-shaped object for a single cluster member so it can be handed
// straight to MilestoneInfoPopup/onInfo, unchanged -- reuses the parent cluster's color/shape
// so every popup opened from one constellation reads as visually related.
function memberToMilestone(parent: Milestone, member: ClusterMember): Milestone {
  return {
    id: member.id,
    year: member.year,
    title: member.title,
    narrative: member.blurb[0],
    posPct: parent.posPct,
    shape: parent.shape,
    color: parent.color,
    ready: true,
    kind: "info",
    blurb: member.blurb,
    blurbImage: member.blurbImage,
  };
}

// Evenly re-spaced (6 -> 96 in steps of 15) across 7 top-level nodes: the 4 events Sian sent
// (previously 4 separate nodes, Sept 15) are now one "constellation" cluster sharing a single
// slot -- see the "early-events" entry below and CultureLine's cluster rendering -- so the line
// is back down from 10 nodes to 7. The horizontal timeline track is still a fixed pixel width
// regardless of milestone count (rather than 100% of viewport) so spacing stays legible
// whenever more nodes get added later -- see TRACK_WIDTH / CultureLine.
const MILESTONES: Milestone[] = [
  { id: "2020", year: "2020", title: "THE BEGINNING", narrative: "An opening into the artistic history of 2020.", posPct: 6, shape: "circle", color: "#d1903f", ready: true },
  {
    id: "early-events",
    year: "Dec 2020 – June 2021",
    // Deliberately no display title for the cluster itself -- each member's own real title
    // only appears in its own popup once clicked, and the node label only ever shows the date
    // range (a fact, not curatorial framing) rather than an invented group name. Sian can
    // supply one if she wants a name to show here.
    title: "",
    narrative: "Dec 2020 – June 2021",
    posPct: 21,
    shape: "diamond",
    color: "#8ea0b5",
    ready: true,
    kind: "cluster",
    clusterMembers: [
      {
        id: "beyond-provenance",
        year: "December 28, 2020",
        title: "Beyond Provenance: $Black Speculative Futures on the Blockchain",
        blurb: [
          "The virtual exhibition brought together 33 Black crypto artists from eight countries.",
          "It challenged prevailing narratives about blockchain-based art by asking who creates, owns, and benefits from the value generated by Black creativity.",
        ],
        blurbImage: "/media/proof-of-culture/early-events/one_off.png",
      },
      {
        id: "clubhouse",
        year: "Early 2021",
        title: "Clubhouse Becomes a Gathering Place",
        blurb: [
          "As digital art markets expanded, Clubhouse became an important gathering place for artists, collectors, curators and organizers. Live conversations, artist talks, educational sessions and launch parties connected people across countries in real time, helping information, opportunities and new work move quickly through the growing community.",
        ],
        blurbImage: "/media/proof-of-culture/early-events/clubhouse.png",
      },
      {
        id: "black-nft-art",
        year: "February 2021",
        title: "Black NFT Art Launches",
        blurb: [
          "Founded by Iris Nevins as part of Umba Daima, Black NFT Art launched as a media and community platform dedicated to increasing visibility, connection, and economic opportunity for Black artists entering the emerging NFT ecosystem.",
            ],
        blurbImage: "/media/proof-of-culture/early-events/black_nft_art.png",
      },
      {
        id: "theblkchain",
        year: "March 2021",
        title: "TheBlkChain Launches",
        blurb: [
          "Founded in 2021, TheBlkChain created an editorial platform focused on documenting and amplifying Black artists, collectors, and builders working across digital art and blockchain culture.",
          "The inaugural \u201cLeaders of The new School\u201d show sold out in 24 hours.",
        ],
        blurbImage: "/media/proof-of-culture/early-events/theblkchain.png",
      },
      {
        id: "ix-shells",
        year: "May 14, 2021",
        title: "IX Shells Sets $2 Million Sales Record",
        blurb: [
          "Panama-born Afro-Caribbean generative artist Itzel Yard, known as IX Shells, sold Dreaming at Dusk for $2 million in support of the Tor Project.",
          "The sale made her the highest-selling female NFT artist at the time.",
        ],
        blurbImage: "/media/proof-of-culture/early-events/ix_shells.png",
      },
      {
        id: "proof-of-sovereignty",
        year: "May 25, 2021",
        title: "Lady Pheønix Presents Proof of Sovereignty",
        blurb: [
          "Christie's collaborated with curator Lady Pheønix to present Proof of Sovereignty, a curated sale bringing together legacy and emerging artists working with blockchain technology.",
          "The project explored sovereignty, freedom, decentralization, and the expanding cultural possibilities of digital ownership.",
          "The works included pieces by Auriea Harvey, Lethabo Huma, and IX Shells.",
        ],
        blurbImage: "/media/proof-of-culture/early-events/proof_of_sovereignty.png",
      },
      {
        id: "digital-diaspora",
        year: "June 2021",
        title: "The Digital Diaspora",
        blurb: [
          "Across platforms and borders, a culture takes shape.",
          "The Digital Diaspora, curated by 17-year-old Diana Sinclair, captured the early movement of Black artists and collectors who were finding one another, and brought much-needed recognition to early Black diasporan artists.",
        ],
        // No logo could be found; Sian supplied this photo of the curator, Diana Sinclair.
        blurbImage: "/media/proof-of-culture/early-events/digital_diaspora.png",
      },
    ],
  },
  { id: "2021-2022", year: "2021", title: "THE EXPANSION", narrative: "Participation, production, experimentation and community expand at extraordinary speed.", posPct: 36, shape: "circle", color: "#e0733a", ready: true },
  { id: "2022", year: "2022", title: "THE DIVERGENCE", narrative: "The market is losing momentum. The art is finding its own.", posPct: 51, shape: "circle", color: "#e0733a", ready: true },
  { id: "2023", year: "2023", title: "AFTER THE MINT", narrative: "The spectacle recedes. The practices remain.", posPct: 66, shape: "circle", color: "#d1903f", ready: true },
  {
    id: "itan-events",
    year: "August 2023",
    title: "",
    narrative: "August 2023",
    posPct: 81,
    shape: "diamond",
    color: "#8ea0b5",
    ready: true,
    kind: "cluster",
    clusterMembers: [
      {
        id: "itan",
        year: "August 2023",
        title: "\u00cct\u00e0n: West African Artistry Launches",
        blurb: [
          "Founded by Sierra Renee, also known as JuuJuu Mama, \u00cct\u00e0n is a West African artist collective bringing Yoruba storytelling and cultural traditions into digital art. Its name means \u201cstory\u201d or \u201chistory\u201d in Yoruba.",
        ],
        blurbImage: "/media/proof-of-culture/2023/itan.png",
      },
    ],
  },
  { id: "present", year: "EPILOGUE", title: "WHAT REMAINS", narrative: "A post is not an archive. A mint is not an archive.", posPct: 96, shape: "circle", color: "#d1903f", ready: true },
];

// The sections you can walk into. The constellation is only a marker on the timeline (dots open
// popups); it is not a room. Used for "Room N of M".
const ROOM_MILESTONES = MILESTONES.filter((m) => m.kind !== "info" && m.kind !== "cluster");

// The constellation's events are part of the walk too, slotted into the room each one falls in,
// in date order, as text cards between works: Beyond Provenance (Dec 28, 2020) closes The
// Beginning; the four events of Feb-June 2021 open The Expansion, before its works. Same words
// and logo as the timeline popups (built from the cluster members themselves).
const EVENTS_IN_ROOMS: Record<string, { at: "start" | "end"; ids: string[] }> = {
  "2020": { at: "end", ids: ["beyond-provenance"] },
  "2021-2022": { at: "start", ids: ["clubhouse", "black-nft-art", "theblkchain", "ix-shells", "proof-of-sovereignty", "digital-diaspora"] },
  "2023": { at: "end", ids: ["itan"] },
};

function withEvents(room: Room, roomId: string): Room {
  const slot = EVENTS_IN_ROOMS[roomId];
  if (!slot) return room;
  // Event sources come from two places: members of any "cluster" milestone (several
  // close-together events sharing one timeline slot, e.g. early-events, itan-events), and any
  // standalone top-level "info" milestone (none currently, but a future one-off event doesn't
  // need a whole cluster just to also show up as a room card) -- normalized to the same
  // {id, year, title, blurb, blurbImage} shape so the lookup below doesn't care which kind of
  // milestone it came from.
  const clusterMembers: ClusterMember[] = [];
  MILESTONES.forEach((x) => {
    if (x.kind === "cluster" && x.clusterMembers) clusterMembers.push(...x.clusterMembers);
  });
  const infoMembers: ClusterMember[] = MILESTONES.filter((x) => x.kind === "info").map((x) => ({
    id: x.id,
    year: x.year,
    title: x.title,
    blurb: x.blurb || [x.narrative],
    blurbImage: x.blurbImage,
  }));
  const members = [...clusterMembers, ...infoMembers];
  const events: EventItem[] = slot.ids
    .map((id) => members.find((x) => x.id === id))
    .filter((x): x is ClusterMember => !!x)
    .map((x) => ({ kind: "event", date: x.year, title: x.title, blurb: x.blurb, image: x.blurbImage }));
  // No countLabel here: MuseumWalkthrough counts works itself (every image, clusters exploded,
  // event cards not counted), so the room title reads e.g. "33 works".
  return {
    ...room,
    items: slot.at === "start" ? [...events, ...room.items] : [...room.items, ...events],
  };
}

const GROUND = "#0b0a09";
// The first room on the timeline -- its node pulses brighter (pocPulseStart in styles.css) as the "start here" cue.
const START_MILESTONE_ID = "2020";

// Each visitor's timeline progress is remembered in their own browser (localStorage), so coming
// back on the same device picks up where they left off. Private to that browser -- nothing is
// sent anywhere. Add ?kiosk to the URL (e.g. on a shared gallery screen) to turn saving off so
// one visitor's progress never carries over to the next. Every read/write is wrapped: if storage
// is blocked (some private-browsing modes) the page just behaves as if nothing was saved.
const VISITED_STORAGE_KEY = "modda-poc-visited-v1";
// Set once a visitor reaches the closing credits; hides "Start over" for anyone who has finished.
const COMPLETED_STORAGE_KEY = "modda-poc-completed-v1";

function loadCompleted(): boolean {
  if (!progressSavingEnabled()) return false;
  try {
    return window.localStorage.getItem(COMPLETED_STORAGE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function saveCompleted() {
  if (!progressSavingEnabled()) return;
  try {
    window.localStorage.setItem(COMPLETED_STORAGE_KEY, "1");
  } catch (_) {
    // storage unavailable
  }
}

function progressSavingEnabled(): boolean {
  try {
    return !new URLSearchParams(window.location.search).has("kiosk");
  } catch (_) {
    return false;
  }
}

function loadVisited(): Set<string> {
  if (!progressSavingEnabled()) return new Set();
  try {
    const raw = window.localStorage.getItem(VISITED_STORAGE_KEY);
    if (!raw) return new Set();
    const ids = JSON.parse(raw);
    if (!Array.isArray(ids)) return new Set();
    const known = new Set(MILESTONES.map((m) => m.id));
    return new Set(ids.filter((id): id is string => typeof id === "string" && known.has(id)));
  } catch (_) {
    return new Set();
  }
}

function saveVisited(visited: Set<string>) {
  if (!progressSavingEnabled()) return;
  try {
    if (visited.size === 0) window.localStorage.removeItem(VISITED_STORAGE_KEY);
    else window.localStorage.setItem(VISITED_STORAGE_KEY, JSON.stringify(Array.from(visited)));
  } catch (_) {
    // storage unavailable -- progress just won't persist
  }
}
// Minimum pixel width for the timeline track (see CultureLine) -- guarantees each node gets at
// least this much physical breathing room regardless of how many milestones exist. 190px/node
// keeps a comfortable gap even where several info-only nodes sit close together in posPct (the
// Dec 2020-June 2021 cluster). On a viewport wider than this minimum, the track (via flex-grow)
// stretches to fill the full width of the screen instead of stopping short of it, so the line --
// and the last node, the Epilogue -- reach all the way to the right edge.
const TRACK_WIDTH = MILESTONES.length * 190;
// Curatorial paragraphs (Book/Regular, Medium for opening lines) and mono-set metadata
// (dates, artwork attribution, room counts, buttons/cues) -- see build notes, Sept 12,
// seventeenth pass. Neue Montreal Text/Mono are Pangram Pangram's commercial fonts and
// there's no license/font files on file yet, so these stacks lead with the real names
// (they'll pick up automatically the moment @font-face rules for them exist) and fall
// back to close free stand-ins in the meantime: General Sans (Fontshare) for Text, IBM
// Plex Mono (Google Fonts, already the "archival ledger" register from the original
// Culture Line prototype) for Mono.
const TEXT_FONT = "'Neue Montreal Text', 'General Sans', Inter, Arial, sans-serif";
const MONO_FONT = "'Neue Montreal Mono', 'IBM Plex Mono', 'Courier New', monospace";
// Small fixed offsets (not random per-render, so they don't jitter on re-render) for the
// tiny constellation of specks around each node.
const SPECKS = [
  { dx: -22, dy: -14, r: 1.6, o: 0.5 },
  { dx: 18, dy: -20, r: 1.2, o: 0.35 },
  { dx: 26, dy: 10, r: 1.4, o: 0.45 },
  { dx: -16, dy: 18, r: 1, o: 0.3 },
  { dx: 6, dy: -26, r: 1.3, o: 0.4 },
  { dx: -30, dy: 4, r: 1, o: 0.3 },
];

// Loose scatter of "constellation" points floating around a central "planet" -- Sian: no
// connecting lines back to the planet, just points of different sizes floating near it, like
// a small starfield rather than a wired diagram. Six slots now that TheBlkChain and Digital Diaspora have
// joined this cluster; if a cluster ever grows past 5, extras stack on the last slot rather
// than crashing -- worth adding more scatter positions at that point.
const CLUSTER_LIFT = 28; // px the whole planet+scatter floats above the timeline line
// Moons sit evenly spaced on one tilted orbit around the planet, in chronological order
// (the members array is already date-ordered): the first event at 12 o'clock, then clockwise.
// Sian: the moons were "kinda scattered" -- an orbit makes the order readable. The tilt matches
// the planet's rings (-18deg); ORBIT_RX/RY keep the lowest moon just above the timeline line.
const ORBIT_RX = 54;
const ORBIT_RY = 24;
const ORBIT_TILT_DEG = -18;
const MOON_SIZE = 8;
function clusterDotOffset(i: number, n: number) {
  const a = -Math.PI / 2 + (i * 2 * Math.PI) / Math.max(n, 1);
  const x = ORBIT_RX * Math.cos(a);
  const y = ORBIT_RY * Math.sin(a);
  const t = (ORBIT_TILT_DEG * Math.PI) / 180;
  return { dx: x * Math.cos(t) - y * Math.sin(t), dy: x * Math.sin(t) + y * Math.cos(t), size: MOON_SIZE };
}

type WorkMeta = {
  title: string;
  artist: string;
  artist_location?: string;
  year: string;
  chain: string;
  contract: string;
  token_id: string;
  medium?: string;
  collection?: string;
  image_file: string;
  // Optional self-hosted video (mp4/webm in the same folder as the stills). When set, the
  // corridor plays it (muted, looping) for the work nearest the viewer; image_file stays as the
  // still shown while it loads and everywhere the video isn't playing. Falls back to the still
  // if the file can't be played.
  video_file?: string;
  // Works sharing the same group_key are clustered into one mosaic slide instead of each
  // getting its own -- e.g. several pieces from the same series. group_label is the caption
  // shown for the cluster as a whole; only needs to be set on (or matches on) the first work
  // carrying that key.
  group_key?: string;
  group_label?: string;
  // Set on any one work in the group (same convention as group_label) to have the corridor
  // show each token's own title on hover -- see WorkGroup.showItemTitles.
  group_show_titles?: boolean;
};

// A paragraph is normally just a string. Every section's copy tends to land on one short,
// standalone thesis line partway through or at the end (2020: "The absence is part of the
// evidence."; 2021-2022: "The culture is not simply being collected. It is being created.") --
// mark that one with { text, emphasis: true } to render it in full white instead of the
// muted grey the rest of the copy uses. Replaces an earlier version of this page that matched
// 2020's specific sentence by exact string comparison, which obviously didn't generalize to
// any other section's copy.
type CopyLine = string | { text: string; emphasis?: boolean };

type SectionData = {
  section: { year: string; title: string; copy: CopyLine[] };
  works: WorkMeta[];
};

type SectionState = { data: SectionData; room: Room } | null;

function isWorkGroup(item: RoomItem): item is WorkGroup {
  return (item as WorkGroup).kind === "group";
}

function toRoom(data: SectionData, folderId: string, positionLabel?: { index: number; total: number }): Room {
  const items: RoomItem[] = [];
  const groups = new Map<string, WorkGroup>();

  // A work still marked PENDING_* has no real image fetched yet -- rendering it anyway just
  // shows a broken "Image Unavailable" placeholder in its slot (confirmed confusing: "is it
  // possible to not show the placeholders for the ones that are missing? ... no bueno").
  // Skip it entirely until a real image file exists; it'll start appearing automatically the
  // moment its image_file in meta.json stops starting with "PENDING_".
  const readyWorks = data.works.filter((w) => !w.image_file.startsWith("PENDING_"));

  readyWorks.forEach((w) => {
    const work: Work = {
      source: "Ethereum",
      chain: w.chain.toLowerCase() as Work["chain"],
      contract: w.contract,
      tokenId: w.token_id,
      artist: captionArtist(w.artist, w.artist_location),
      title: w.title.trim(),
      year: w.year,
      medium: w.medium,
      collection: w.collection,
      media: `/media/proof-of-culture/${folderId}/${w.image_file}`,
      mediaType: "image",
      ...(w.video_file ? { animation: `/media/proof-of-culture/${folderId}/${w.video_file}` } : {}),
    };

    if (w.group_key) {
      let group = groups.get(w.group_key);
      if (!group) {
        group = { kind: "group", label: w.group_label || w.group_key, works: [] };
        groups.set(w.group_key, group);
        items.push(group); // reserves the cluster's slot at its first work's position
      }
      if (w.group_show_titles) group.showItemTitles = true;
      group.works.push(work);
    } else {
      items.push(work);
    }
  });

  // A group that lost most of its works to the PENDING filter above (e.g. AfroDroids: 1 real
  // image, 3 still pending) no longer reads as a cluster -- show its lone survivor as a normal
  // single-work slide instead of a one-cell mosaic captioned "· 1 works".
  const resolvedItems = items.map((item) => (isWorkGroup(item) && item.works.length === 1 ? item.works[0] : item));

  // No description here: the full curatorial copy is already shown on the Threshold page
  // just before this corridor mounts. Repeating it on the corridor's own room-title slide
  // was the "the copy shows twice" bug -- this slide now only carries the title + count.
  return { title: data.section.title, items: resolvedItems, positionLabel };
}

type Stage = "line" | "copy" | "works" | "credits" | "dark";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

// Phone layout for the timeline: the same milestones run top-to-bottom down a thin vertical
// line (the wide horizontal track would hide four of the six sections off-screen on a phone).
// Each section is one big tap target. The Dec 2020 - June 2021 constellation keeps its planet
// and rings on the line, and its six events hang beneath it as tappable star rows (the tiny
// scattered star points are too small to hit reliably with a thumb).
function MobileTimeline({
  visited,
  onEnter,
  onInfo,
  lastVisitedPct,
}: {
  visited: Set<string>;
  onEnter: (m: Milestone) => void;
  onInfo: (m: Milestone) => void;
  lastVisitedPct: number;
}) {
  const LINE_X = 30; // px from the left edge of the timeline area to the centre of the line
  return (
    <div style={{ position: "relative", padding: "12px 24px 96px" }}>
      {MILESTONES.map((m, idx) => {
        const isLast = idx === MILESTONES.length - 1;
        const isVisited = visited.has(m.id);
        const lit = isVisited || m.ready;
        const segmentLit = m.posPct < lastVisitedPct;
        const isCluster = m.kind === "cluster" && !!m.clusterMembers;
        return (
          <div key={m.id} style={{ position: "relative", paddingLeft: LINE_X + 30, paddingBottom: isLast ? 0 : 40, minHeight: 56 }}>
            {/* line segment from this node down to the next */}
            {!isLast && (
              <div
                style={{
                  position: "absolute",
                  left: LINE_X - 1,
                  top: 14,
                  bottom: -14,
                  width: 2,
                  background: segmentLit ? "linear-gradient(180deg, #d1903f, #e0733a)" : "rgba(255,255,255,0.12)",
                }}
              />
            )}
            {/* node */}
            {isCluster ? (
              <div style={{ position: "absolute", left: LINE_X, top: 14, width: 0, height: 0 }}>
                <div style={{ position: "absolute", left: 0, top: 0, transform: "translate(-50%, -50%) rotate(-18deg)", width: 44, height: 15, borderRadius: "50%", border: `1px solid ${m.color}`, opacity: 0.5 }} />
                <div style={{ position: "absolute", left: 0, top: 0, transform: "translate(-50%, -50%) rotate(-18deg)", width: 60, height: 21, borderRadius: "50%", border: `1px solid ${m.color}`, opacity: 0.28 }} />
                <div style={{ position: "absolute", left: 0, top: 0, transform: "translate(-50%, -50%)", width: 18, height: 18, borderRadius: "50%", background: m.color, boxShadow: `0 0 14px ${m.color}, inset 0 0 4px rgba(255,255,255,0.5)` }} />
              </div>
            ) : (
              <button
                onClick={() => {
                  if (!m.ready) return;
                  if (m.kind === "info") onInfo(m);
                  else onEnter(m);
                }}
                disabled={!m.ready}
                aria-label={m.title}
                style={{
                  position: "absolute",
                  left: LINE_X,
                  top: 14,
                  transform: "translate(-50%, -50%)",
                  width: 44,
                  height: 44,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: m.ready ? "pointer" : "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: lit ? m.color : "rgba(255,255,255,0.25)",
                    color: m.color,
                    boxShadow: m.ready ? `0 0 10px ${m.color}` : "none",
                    animation: m.id === START_MILESTONE_ID ? "pocPulseStart 2.2s ease-in-out infinite" : m.ready ? "pocPulse 2.6s ease-in-out infinite" : "none",
                  }}
                />
              </button>
            )}

            {isCluster ? (
              <div>
                <div style={{ fontFamily: MONO_FONT, fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", paddingTop: 4 }}>{m.year}</div>
                <div style={{ marginTop: 10 }}>
                  {m.clusterMembers!.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => onInfo(memberToMilestone(m, member))}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        width: "100%",
                        minHeight: 48,
                        padding: "8px 0",
                        background: "none",
                        border: "none",
                        textAlign: "left",
                        cursor: "pointer",
                        color: "#f2f2f2",
                      }}
                    >
                      <span
                        style={{
                          flex: "0 0 auto",
                          marginTop: 8,
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: m.color,
                          border: "1.5px solid rgba(255,255,255,0.8)",
                          boxShadow: `0 0 8px ${m.color}`,
                        }}
                      />
                      <span>
                        <span style={{ display: "block", fontFamily: MONO_FONT, fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#888" }}>{member.year}</span>
                        <span style={{ display: "block", fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "19px", lineHeight: 1.25, marginTop: 2, fontVariantNumeric: "lining-nums" }}>{member.title}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  if (!m.ready) return;
                  if (m.kind === "info") onInfo(m);
                  else onEnter(m);
                }}
                disabled={!m.ready}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  padding: "0 0 0 0",
                  cursor: m.ready ? "pointer" : "default",
                  color: m.ready ? "#f2f2f2" : "#666",
                }}
              >
                <span style={{ display: "block", fontFamily: MONO_FONT, fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", paddingTop: 4 }}>{m.year}</span>
                {m.title && (
                  <span style={{ display: "block", fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "26px", marginTop: 4, lineHeight: 1.15 }}>
                    {m.shortTitle || m.title}
                  </span>
                )}
                <span style={{ display: "block", fontFamily: "'EB Garamond', serif", fontSize: "15px", color: "#8f8f8f", marginTop: 6, lineHeight: 1.4 }}>{m.narrative}</span>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CultureLine({
  visited,
  onEnter,
  onInfo,
  onStartOver,
  completed,
}: {
  visited: Set<string>;
  completed: boolean;
  onEnter: (m: Milestone) => void;
  onInfo: (m: Milestone) => void;
  onStartOver: () => void;
}) {
  const isMobile = useIsMobile();
  const lastVisitedPct = Math.max(0, ...MILESTONES.filter((m) => visited.has(m.id)).map((m) => m.posPct));

  return (
    <div style={{ minHeight: "100vh", background: GROUND, color: "#f2f2f2", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: isMobile ? "32px 24px 32px" : "44px 44px 32px" }}>
        <span style={{ fontFamily: MONO_FONT, fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#888" }}>Exhibition</span>
        <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, fontSize: isMobile ? "44px" : "72px", margin: "10px 0 0" }}>
          Proof of Culture
        </h1>
        <p style={{ margin: "10px 0 0", fontFamily: "Cormorant Garamond, serif", fontWeight: 400, fontStyle: "italic", fontSize: isMobile ? "19px" : "22px", color: "#8f8f8f" }}>
          Curated by Sian A. Morson
        </p>
        <p style={{ margin: isMobile ? "28px 0 0" : "36px 0 0", fontFamily: "'EB Garamond', serif", fontWeight: 400, fontStyle: "normal", fontSize: isMobile ? "15px" : "16px", lineHeight: 1.7, color: "#d0d0d0", maxWidth: isMobile ? "340px" : "760px" }}>
          Proof of Culture takes its name from what these works make visible. From 2020 through 2023, Black artists across the diaspora were using digital tools to make art, tell stories, find one another, and build new spaces for their work. The works in this exhibition are evidence of that cultural moment: what artists created, and the relationships, experiments, and shared ambition that grew around it. Together, they form part of a history that deserves to be seen, studied, and preserved.
        </p>
        <p style={{ margin: isMobile ? "14px 0 28px" : "16px 0 36px", fontFamily: "'EB Garamond', serif", fontWeight: 400, fontStyle: "italic", fontSize: isMobile ? "15px" : "16px", lineHeight: 1.7, color: "#9a9a9a", maxWidth: isMobile ? "340px" : "760px" }}>
          Explore the exhibition chronologically, or move between years at your own pace.
          {visited.size > 0 && !completed && (
            <>
              {" "}
              <button
                onClick={onStartOver}
                style={{ background: "none", border: "none", padding: 0, marginLeft: "6px", fontFamily: MONO_FONT, fontStyle: "normal", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer" }}
              >
                Start over ↺
              </button>
            </>
          )}
        </p>
      </div>

      {isMobile ? (
        <MobileTimeline visited={visited} onEnter={onEnter} onInfo={onInfo} lastVisitedPct={lastVisitedPct} />
      ) : (
      <div style={{ flex: 1, display: "flex", alignItems: "center", minHeight: "260px", padding: isMobile ? "32px 24px 100px" : "32px 60px 120px", overflow: "auto visible" }}>
        <div style={{ position: "relative", flex: "1 0 auto", minWidth: `${TRACK_WIDTH}px`, height: "2px", background: "rgba(255,255,255,0.1)" }}>
          <div style={{ position: "absolute", left: 0, top: 0, height: "2px", width: `${lastVisitedPct}%`, background: "linear-gradient(90deg, #d1903f, #e0733a)" }} />

          {MILESTONES.map((m) => {
            const isVisited = visited.has(m.id);
            const size = m.shape === "diamond" ? 14 : 12;
            const lit = isVisited || m.ready;
            return (
              <div key={m.id} style={{ position: "absolute", left: `${m.posPct}%`, top: 0, transform: "translate(-50%, -50%)", textAlign: "center", width: "180px" }}>
                {/* tiny constellation of specks around the node */}
                {SPECKS.map((s, i) => (
                  <span
                    key={i}
                    style={{
                      position: "absolute",
                      left: `calc(50% + ${s.dx}px)`,
                      top: `calc(50% + ${s.dy}px)`,
                      width: s.r * 2,
                      height: s.r * 2,
                      borderRadius: "50%",
                      background: lit ? m.color : "#666",
                      opacity: lit ? s.o : s.o * 0.5,
                      animation: lit ? `pocPulse ${2.6 + i * 0.3}s ease-in-out infinite` : "none",
                      pointerEvents: "none",
                    }}
                  />
                ))}
                {m.kind === "cluster" && m.clusterMembers ? (
                  // Take four: fully above the line, same blue-grey planet, decorative
                  // Saturn-style rings, and the real events floating loose around it with no
                  // wires back to the planet or down to the timeline -- Sian asked for the
                  // last connecting line (planet-to-timeline) gone too, so the whole cluster
                  // now reads as a free-floating group. The clickable star points get a
                  // bright white halo border (the planet and rings have none) so it's clear
                  // at a glance which dots are buttons and which are decoration.
                  <>
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: `calc(50% - ${CLUSTER_LIFT}px)`,
                        transform: "translate(-50%, -50%) rotate(-18deg)",
                        width: 44,
                        height: 15,
                        borderRadius: "50%",
                        border: `1px solid ${m.color}`,
                        opacity: 0.5,
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: `calc(50% - ${CLUSTER_LIFT}px)`,
                        transform: "translate(-50%, -50%) rotate(-18deg)",
                        width: 60,
                        height: 21,
                        borderRadius: "50%",
                        border: `1px solid ${m.color}`,
                        opacity: 0.28,
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: `calc(50% - ${CLUSTER_LIFT}px)`,
                        transform: `translate(-50%, -50%) rotate(${ORBIT_TILT_DEG}deg)`,
                        width: ORBIT_RX * 2,
                        height: ORBIT_RY * 2,
                        borderRadius: "50%",
                        border: `1px dashed ${m.color}`,
                        opacity: 0.2,
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                    <div
                      title={`${m.clusterMembers.length} notable events -- click a point to explore each one`}
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: `calc(50% - ${CLUSTER_LIFT}px)`,
                        transform: "translate(-50%, -50%)",
                        zIndex: 2,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: m.color,
                        boxShadow: `0 0 14px ${m.color}, inset 0 0 4px rgba(255,255,255,0.5)`,
                        // The planet itself holds steady; only the clickable points around it twinkle.
                        pointerEvents: "none",
                      }}
                    />
                    {m.clusterMembers.map((member, i) => {
                      const offset = clusterDotOffset(i, m.clusterMembers!.length);
                      return (
                        <button
                          key={member.id}
                          className="poc-cluster-star"
                          onClick={() => onInfo(memberToMilestone(m, member))}
                          title={member.title}
                          style={{
                            position: "absolute",
                            left: `calc(50% + ${offset.dx}px)`,
                            top: `calc(50% - ${CLUSTER_LIFT}px + ${offset.dy}px)`,
                            transform: "translate(-50%, -50%)",
                            zIndex: 3,
                            width: offset.size,
                            height: offset.size,
                            borderRadius: "50%",
                            background: m.color,
                            border: "1.5px solid rgba(255,255,255,0.8)",
                            cursor: "pointer",
                            boxShadow: `0 0 8px ${m.color}`,
                            // Staggered so the points twinkle one after another instead of in unison:
                            // same rhythm, each offset by an even slice of the cycle (negative delay =
                            // already out of step on first paint).
                            animation: `pocPulse 2.8s ease-in-out ${(-(i * 2.8) / m.clusterMembers!.length).toFixed(2)}s infinite`,
                            padding: 0,
                          }}
                        />
                      );
                    })}
                  </>
                ) : (
                  <button
                    onClick={() => {
                      if (!m.ready) return;
                      if (m.kind === "info") onInfo(m);
                      else onEnter(m);
                    }}
                    disabled={!m.ready}
                    title={m.ready ? undefined : "Not yet built"}
                    style={{
                      position: "relative",
                      zIndex: 1,
                      width: size, height: size,
                      borderRadius: m.shape === "diamond" ? 0 : "50%",
                      transform: m.shape === "diamond" ? "rotate(45deg)" : "none",
                      background: lit ? m.color : "rgba(255,255,255,0.25)",
                      border: "none",
                      cursor: m.ready ? "pointer" : "default",
                      // THE BEGINNING pulses brighter than the rest so visitors know where to start.
                      color: m.color,
                      boxShadow: m.ready ? `0 0 10px ${m.color}` : "none",
                      animation: m.id === START_MILESTONE_ID ? "pocPulseStart 2.2s ease-in-out infinite" : m.ready ? "pocPulse 2.6s ease-in-out infinite" : "none",
                      padding: 0,
                    }}
                  />
                )}
                <div style={{ fontFamily: MONO_FONT, marginTop: "30px", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", position: "relative", zIndex: 1 }}>{m.year}</div>
                {m.title && (
                  <div style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "18px", marginTop: "4px", color: m.ready ? "#f2f2f2" : "#666", position: "relative", zIndex: 1 }}>
                    {m.shortTitle || m.title}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}

// A lightweight popup for a milestone that isn't a themed room -- just a short blurb about a
// single event (Digital Diaspora, June 2021). Dismissed by the close button, a click on the
// backdrop, or Escape -- same pattern as the Index overlay in MuseumWalkthrough.tsx.
function MilestoneInfoPopup({ m, onClose }: { m: Milestone; onClose: () => void }) {
  const isMobile = useIsMobile();
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    window.setTimeout(onClose, 400);
  }, [onClose]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  return (
    <div
      onClick={handleClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,9,8,0.86)",
        zIndex: 50,
        display: "flex",
        // margin:auto on the card (below) centres it when it fits and lets the whole overlay
        // scroll when it doesn't -- align-items:center alone would clip the top of a tall card
        // (Beyond Provenance, Proof of Sovereignty) on a phone.
        overflowY: "auto",
        overscrollBehavior: "contain",
        padding: isMobile ? "24px" : "40px",
        opacity: closing ? 0 : 1,
        transition: "opacity 400ms ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          margin: "auto",
          maxWidth: "520px",
          width: "100%",
          background: GROUND,
          border: `1px solid ${m.color}55`,
          borderRadius: "4px",
          padding: isMobile ? "40px 28px 32px" : "48px 44px 40px",
          textAlign: "center",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <button
          onClick={handleClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "transparent",
            border: "none",
            color: "#888",
            fontFamily: MONO_FONT,
            fontSize: "10px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            cursor: "pointer",
            padding: "4px",
          }}
        >
          Close
        </button>
        {/* Logo/visual: round, in the card's upper-right corner (below the Close button). The date
            row reserves the badge's height so the title below never runs under it. */}
        {m.blurbImage && (
          <div style={{ position: "absolute", top: isMobile ? "44px" : "52px", right: isMobile ? "28px" : "44px" }}>
            <RoundBadge src={m.blurbImage} size={isMobile ? 56 : 72} />
          </div>
        )}
        <div style={{ minHeight: m.blurbImage ? (isMobile ? "56px" : "72px") : undefined }}>
          <span style={{ fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#888", fontFamily: MONO_FONT }}>
            {m.year}
          </span>
        </div>

        <h3
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontWeight: 400,
            fontStyle: "italic",
            fontSize: isMobile ? "28px" : "36px",
            fontVariantNumeric: "lining-nums", // keeps numerals in milestone popup titles reading as digits, not letters
            margin: "14px 0 22px",
            color: "#f2f2f2",
          }}
        >
          {m.title}
        </h3>
        {(m.blurb || [m.narrative]).map((line, i) => (
          <p
            key={i}
            style={{
              // Same serif as the threshold-page copy, one style for every paragraph of every
              // card (a larger italic lead on the first paragraph made cards look different in
              // size depending on how long their first paragraph was -- Sian's consistency ask).
              fontFamily: "'EB Garamond', serif",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: isMobile ? "18px" : "20px",
              lineHeight: 1.7,
              color: "#d0d0d0",
              margin: i === 0 ? 0 : "14px 0 0",
            }}
          >
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

// The one threshold page between the timeline and the works corridor: the curatorial copy
// reads first, one paragraph fading in after another, then a "scroll to begin" cue takes
// over from what used to be a separate title + "Enter this threshold" button page (removed
// per Sian's "we don't need 3 steps").
//
// Advancing on scroll/swipe/arrow-key here is safe even though an earlier round of this
// component deliberately avoided it: the concern back then was Mac trackpad inertial
// momentum carrying a scroll gesture forward into the corridor before its own scroll math
// was ready. That's now handled downstream regardless of what triggers the move into
// "works" -- the parent's stage effect resets scrollY to 0 and holds document scroll
// locked for ~400ms after "works" mounts, which absorbs leftover momentum from whatever
// gesture (click or scroll) caused the transition. A short arm delay below additionally
// guards against a stray residual wheel/touch event from the gesture that opened *this*
// page (e.g. clicking a timeline node) firing an instant, unintended skip.
// Pacing of the threshold copy page. Sian: the copy "doesn't stay on the page long enough to
// read ... the scroll and fade there should be slowed down significantly." Paragraphs now arrive
// one per second and fade in over 1.8s; the page won't respond to scrolling until they have all
// landed plus a dwell, the reader must rest at the bottom of the copy before scrolling counts,
// and the crossfade out to the corridor takes 2s (was 1.3s).
const COPY_STAGGER_MS = 1000;
const COPY_FADE_MS = 1800;
const COPY_DWELL_MS = 400; // rest at the bottom before a scroll counts (was 1500)
// The "Scroll to continue" cue used to wait for the last paragraph to finish AND the dwell
// (about 2.5s of nothing after the text was fully in). It now starts fading in as the last
// paragraph is landing, so the reader sees it almost as soon as they reach the end.
const COPY_CUE_DELAY_MS = 200; // added to (paragraph count x stagger)
const COPY_EXIT_MS = 2000;

function CopyPage({
  m,
  section,
  onAdvance,
  exiting = false,
}: {
  m: Milestone;
  section: SectionState;
  onAdvance: () => void;
  // True while this page is crossfading to black ahead of a "goes dark" ending (see
  // DarkEnding below) instead of cutting into the works corridor. Optional so every other
  // call site (every room section) is unaffected.
  exiting?: boolean;
}) {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!section) return;
    let armed = false;
    let advanced = false;
    let touchStartY: number | null = null;

    function nearBottom() {
      const el = containerRef.current;
      if (!el) return true;
      // A section whose copy fits in one viewport has nothing left to scroll past --
      // scrollHeight ~= clientHeight there, so this is true immediately, same as before.
      return el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    }

    // Arm only once every paragraph has finished fading in PLUS a dwell -- this is also the
    // moment the "Scroll to continue" cue below appears.
    const readDelayMs = section.data.section.copy.length * COPY_STAGGER_MS + COPY_FADE_MS + COPY_DWELL_MS;
    const armTimer = window.setTimeout(() => {
      armed = true;
    }, readDelayMs);

    // Scroll input only counts once the reader has been resting at the bottom of the copy for a
    // moment, and then only as a deliberate push (accumulated distance / a real swipe) -- not
    // the tail of the momentum that carried them down to the last paragraph, which used to
    // trip the advance the instant the bottom was reached.
    const SETTLE_MS = 1200;
    const PUSH_PX = 260;
    let bottomSince: number | null = nearBottom() ? performance.now() : null;
    let accum = 0;
    let accumTimer: number | undefined;
    function noteBottom() {
      if (nearBottom()) {
        if (bottomSince == null) bottomSince = performance.now();
      } else {
        bottomSince = null;
        accum = 0;
      }
    }
    function deliberate() {
      return bottomSince != null && performance.now() - bottomSince > SETTLE_MS;
    }

    function tryAdvance() {
      if (!armed || advanced) return;
      advanced = true;
      onAdvance();
    }

    function onWheel(e: WheelEvent) {
      noteBottom();
      if (e.deltaY <= 0 || !deliberate()) return;
      accum += e.deltaY;
      window.clearTimeout(accumTimer);
      accumTimer = window.setTimeout(() => {
        accum = 0;
      }, 500);
      if (accum > PUSH_PX) tryAdvance();
    }
    function onTouchStart(e: TouchEvent) {
      touchStartY = e.touches[0]?.clientY ?? null;
    }
    function onTouchMove(e: TouchEvent) {
      if (touchStartY == null) return;
      noteBottom();
      const dy = touchStartY - (e.touches[0]?.clientY ?? touchStartY);
      if (dy > 70 && deliberate()) tryAdvance();
    }
    function onKey(e: KeyboardEvent) {
      noteBottom();
      if ((e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") && deliberate()) tryAdvance();
    }
    const box = containerRef.current;
    box?.addEventListener("scroll", noteBottom, { passive: true });

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(armTimer);
      window.clearTimeout(accumTimer);
      box?.removeEventListener("scroll", noteBottom);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKey);
    };
  }, [section, onAdvance]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed",
        inset: 0,
        background: GROUND,
        color: "#f2f2f2",
        overflowY: "auto",
        zIndex: 40,
        opacity: exiting ? 0 : 1,
        // While this page is crossfading out (see COPY_EXIT_MS above), it stays mounted -- and
        // at zIndex 40, above everything -- for the full fade. It was still `pointerEvents:
        // "auto"` (the CSS default) that whole time, so it kept intercepting every tap for the
        // full fade duration even though it was visually disappearing. Explicitly turning that
        // off the moment it starts exiting means whatever's arriving underneath (the corridor,
        // or DarkEnding) is never blind-blocked by an invisible layer still sitting on top of it.
        pointerEvents: exiting ? "none" : "auto",
        transition: exiting ? `opacity ${COPY_EXIT_MS}ms ease` : "none",
      }}
    >
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: isMobile ? "60px 24px" : "80px 24px" }}>
        {/* The year label used to pop in at full brightness the instant the page mounted, ahead of the
            copy -- read as "a small flash before each text section that shows a date". It now
            fades up on its own slow beat, just before the first paragraph. */}
        <span style={{ fontSize: "12px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#888", fontFamily: MONO_FONT, opacity: 0, animation: "pocFadeUp 1600ms ease both" }}>{m.year}</span>

        {!section ? null : (
          <div style={{ maxWidth: "620px", marginTop: "30px" }}>
            {section.data.section.copy.map((para, i) => {
              const text = typeof para === "string" ? para : para.text;
              const emphasis = typeof para === "string" ? false : !!para.emphasis;
              // Opening line of the section's copy reads as its own beat -- Sian asked for a
              // genuinely different FONT here, not just a heavier weight of the body face. It
              // now reuses EB Garamond italic, the same serif already used site-wide for
              // standalone statement lines (App.tsx's Manifesto page: "Culture is not
              // temporary." / "This is MoDDA.") -- distinct from both the Cormorant Garamond
              // titles and the Neue Montreal Text body copy, and already loaded/licensed, so
              // no new font to source. Every other body paragraph stays plain Book/Regular
              // Neue Montreal Text; the closing thesis line (emphasis) keeps its own italic
              // treatment in that same body face, unchanged.
              // Only a short first line reads as a section "opener" (e.g. "The record begins."). The
              // 2021 section has no separate opener -- its first paragraph is a full body
              // paragraph -- and was being blown up to the opener size, making its header copy
              // look larger than every other section's. Long first paragraphs use body style.
              const isOpening = i === 0 && !emphasis && text.length <= 90;
              return (
                <p
                  key={i}
                  style={{
                    // Sian: "the copy ... all need to be stylized not just the header" -- every
                    // paragraph now uses the EB Garamond serif (previously only the opener did,
                    // with the body in the plain sans). Opener and closing thesis line stay
                    // italic and larger; body paragraphs are upright, a step down in size and
                    // brightness so the two special lines still stand out.
                    fontFamily: "'EB Garamond', serif",
                    fontWeight: isOpening || emphasis ? 500 : 400,
                    fontStyle: isOpening || emphasis ? "italic" : "normal",
                    fontSize: isOpening ? "26px" : emphasis ? "24px" : "19px",
                    lineHeight: isOpening ? 1.5 : emphasis ? 1.5 : 1.75,
                    letterSpacing: "0.005em",
                    color: emphasis || isOpening ? "#f2f2f2" : "#bdbdbd",
                    margin: "0 0 20px",
                    opacity: 0,
                    animation: `pocFadeUp ${COPY_FADE_MS}ms ease ${i * COPY_STAGGER_MS}ms both`,
                  }}
                >
                  {text}
                </p>
              );
            })}
            <div
              style={{
                marginTop: "36px",
                fontFamily: MONO_FONT,
                fontSize: "11px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#777",
                opacity: 0,
                animation: `pocFadeUp 1400ms ease ${section.data.section.copy.length * COPY_STAGGER_MS + COPY_CUE_DELAY_MS}ms both, pocPulse 2.6s ease-in-out ${section.data.section.copy.length * COPY_STAGGER_MS + COPY_CUE_DELAY_MS + 1400}ms infinite`,
              }}
            >
              Scroll to continue ↓
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// A milestone whose room has zero works -- currently only the closing "Epilogue" section --
// has nothing to walk a visitor through after its copy page. Rather than mounting an empty
// MuseumWalkthrough corridor (which would just show a room-title slide reading "· 0 works"),
// the experience fades to black and holds there as the exhibition's end state. A quiet
// "Return to the timeline" cue fades in after a pause so no one is stranded on a black
// screen -- clicking it (or the screen, once the cue has appeared) goes back to the line.
// ---------------------------------------------------------------------------------------------
// Artist credits: a slow-rolling list of every artist in the exhibition and their country, shown
// after the last section and before the closing "Proof of Culture" screen. Built from the same
// meta.json files the corridor reads, so it can never drift from what was actually shown: hidden
// (PENDING_) works don't count, "Artist Unknown" is left out, and a collection name used as an
// "artist" (Ancient Warriors) is not a person, so it is left out too.
// ---------------------------------------------------------------------------------------------
type Credit = { name: string; country: string };
type CreditsData = { credits: Credit[]; totalWorks: number };

const CREDIT_SECTIONS = ["2020", "2021-2022", "2022", "2023"];
const CREDIT_EXCLUDE = new Set(["ancient warriors"]);
// nygilia's handle used to be kept lowercase here to match her objkt handle and work caption,
// with a one-off CREDIT_DISPLAY map capitalizing it just for this credits roll -- Sian later
// asked for "Nygilia" capitalized everywhere, so the artist field itself was fixed at the
// source (2023/meta.json) instead, and this special case is gone.

// Countries are written out in full wherever they are shown (credits roll and the artist line under
// each work) -- museum labels don't use "USA"/"UK". The meta.json data keeps whatever short form
// it has; only the display changes.
const COUNTRY_DISPLAY: Record<string, string> = {
  usa: "United States",
  "u.s.a.": "United States",
  "u.s.": "United States",
  us: "United States",
  uk: "United Kingdom",
  "u.k.": "United Kingdom",
  "antigua & barbuda": "Antigua and Barbuda",
};
function displayCountry(c: string): string {
  return COUNTRY_DISPLAY[c.trim().toLowerCase()] || c;
}
// "Name" + country from meta.json -> caption text. The country is either its own field or the tail
// of the artist string ("Name, Country") in the 2022/2023 files.
function captionArtist(artist: string, location?: string): string {
  if (location) {
    const c = displayCountry(tidyCountry(location));
    return c ? `${artist}, ${c}` : artist;
  }
  const i = artist.lastIndexOf(", ");
  if (i > 0) {
    const c = displayCountry(tidyCountry(artist.slice(i + 2)));
    return c ? `${artist.slice(0, i)}, ${c}` : artist.slice(0, i);
  }
  return artist;
}

function tidyCountry(raw: string): string {
  const c = raw.trim();
  if (!c || /unknown/i.test(c)) return "";
  // "CANADA" -> "Canada"; short codes (USA, UK) stay as they are.
  if (c === c.toUpperCase() && c.length > 3) return c.charAt(0) + c.slice(1).toLowerCase();
  return c;
}

async function loadCredits(): Promise<CreditsData> {
  const all: SectionData[] = await Promise.all(
    CREDIT_SECTIONS.map((id) =>
      fetch(`/media/proof-of-culture/${id}/meta.json?v=${Date.now()}`, { cache: "no-store" }).then((r) => {
        if (!r.ok) throw new Error(`status ${r.status}`);
        return r.json();
      })
    )
  );
  // Every work counts once here, same as the walkthrough's room totals (itemWorkCount /
  // roomWorkCount in MuseumWalkthrough.tsx): each meta.json entry is one image, including every
  // member of a mosaic/cluster group, so a plain count of non-PENDING_ entries matches "74 works"
  // without needing to know which works are grouped.
  let totalWorks = 0;
  const byKey = new Map<string, Credit>();
  all.forEach((data) =>
    data.works.forEach((w) => {
      if (w.image_file.startsWith("PENDING_")) return;
      totalWorks += 1;
      let name = w.artist.trim();
      let country = w.artist_location || "";
      if (!country) {
        // 2022 and 2023 keep the country inside the artist field: "Name, Country".
        const i = name.lastIndexOf(", ");
        if (i > 0) {
          country = name.slice(i + 2);
          name = name.slice(0, i);
        }
      }
      // A joint credit ("Jahmel Reynolds & Latashá", "Heno. + Andre Oshea") lists each artist.
      name.split(/ & | \+ /).forEach((part) => {
        // "Heno." (as in the minted title) and "Heno" are the same person.
        const cleaned = part.trim().replace(/\.$/, "");
        if (!cleaned || /^artist unknown$/i.test(cleaned) || CREDIT_EXCLUDE.has(cleaned.toLowerCase())) return;
        const n = cleaned;
        const key = n.toLowerCase();
        const prev = byKey.get(key);
        const c = displayCountry(tidyCountry(country));
        if (!prev) byKey.set(key, { name: n, country: c });
        else if (!prev.country && c) prev.country = c;
      });
    })
  );
  // Alphabetized ignoring a leading "The" (library/museum convention), so "The Creator Deji"
  // files under C, not T -- otherwise a name like that hides at the end of the alphabet, far
  // from where anyone would look for it.
  const sortKey = (n: string) => n.replace(/^the\s+/i, "");
  const credits = Array.from(byKey.values()).sort((a, b) => sortKey(a.name).localeCompare(sortKey(b.name), "en", { sensitivity: "base" }));
  return { credits, totalWorks };
}

function ArtistCredits({ onDone }: { onDone: () => void }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState<CreditsData | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const scroller = useRef<HTMLDivElement | null>(null);
  const auto = useRef(true);
  const leavingRef = useRef(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const finish = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setLeaving(true);
    window.setTimeout(() => doneRef.current(), 700);
  }, []);

  useEffect(() => {
    let alive = true;
    loadCredits()
      .then((d) => alive && setData(d))
      .catch(() => doneRef.current()); // if the lists can't be read, skip straight on to the ending
    const t = window.setTimeout(() => setVisible(true), 50);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, []);

  // Slow automatic roll, like end credits. Any scroll, touch or key from the visitor takes over.
  useEffect(() => {
    if (!data) return;
    const el = scroller.current;
    if (!el) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) auto.current = false;
    const stop = () => {
      auto.current = false;
    };
    el.addEventListener("wheel", stop, { passive: true });
    el.addEventListener("touchstart", stop, { passive: true });
    el.addEventListener("pointerdown", stop);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish();
      else stop();
    };
    window.addEventListener("keydown", onKey);

    const speed = isMobile ? 56 : 70; // px per second
    let pos = 0;
    let last = performance.now();
    let raf = 0;
    let endedAt = 0;
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const max = el.scrollHeight - el.clientHeight;
      if (auto.current) {
        pos = Math.min(max, pos + speed * dt);
        el.scrollTop = pos;
        if (pos >= max) {
          if (!endedAt) endedAt = now;
          if (now - endedAt > 1500) finish();
        }
      } else {
        pos = el.scrollTop;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("wheel", stop);
      el.removeEventListener("touchstart", stop);
      el.removeEventListener("pointerdown", stop);
      window.removeEventListener("keydown", onKey);
    };
  }, [data, isMobile, finish]);

  const countries = data ? new Set(data.credits.map((c) => c.country).filter(Boolean)).size : 0;

  return (
    <div
      ref={scroller}
      className="poc-credits"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        background: "#000",
        overflowY: "auto",
        opacity: visible && !leaving ? 1 : 0,
        transition: "opacity 700ms ease",
      }}
    >
      {data && (
        <div style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "0 24px" : "0 44px", textAlign: "center" }}>
          <div style={{ height: "70vh" }} />
          <div style={{ fontFamily: MONO_FONT, fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#888" }}>Artists</div>
          <div style={{ fontFamily: "'EB Garamond', serif", fontSize: isMobile ? "16px" : "18px", color: "#9a9a9a", margin: "12px 0 64px" }}>
            {data.totalWorks} works by {data.credits.length} artists and collectives{countries ? ` from ${countries} countries` : ""}
          </div>
          {data.credits.map((c) => (
            <div key={c.name} style={{ marginBottom: isMobile ? 30 : 36 }}>
              <div
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontStyle: "italic",
                  fontSize: isMobile ? "26px" : "32px",
                  lineHeight: 1.2,
                  color: "#f2f2f2",
                  fontVariantNumeric: "lining-nums",
                }}
              >
                {c.name}
              </div>
              {c.country && (
                <div style={{ fontFamily: MONO_FONT, fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#777", marginTop: 6 }}>{c.country}</div>
              )}
            </div>
          ))}
          <div style={{ height: "40vh" }} />
        </div>
      )}
      {/* On a phone the names roll right under the Continue button and collided with it, so
          a soft black fade at the foot of the screen lets them dissolve before they reach it. */}
      {isMobile && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            height: 96,
            background: "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.92) 65%)",
            pointerEvents: "none",
          }}
        />
      )}
      <button
        onClick={finish}
        style={{
          position: "fixed",
          right: isMobile ? 20 : 36,
          bottom: isMobile ? 22 : 32,
          background: "transparent",
          border: "none",
          fontFamily: MONO_FONT,
          fontSize: "11px",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "#888",
          cursor: "pointer",
          padding: 8,
        }}
      >
        Continue →
      </button>
    </div>
  );
}

function DarkEnding({ onReturn }: { onReturn: () => void }) {
  const navigate = useNavigate();
  const [showButtons, setShowButtons] = useState(false);

  // "Proof of Culture" surfaces first, alone in the dark, then the two exits fade in a beat later so
  // the ending gets a moment of silence before offering a way out.
  useEffect(() => {
    const t = window.setTimeout(() => setShowButtons(true), 2600);
    return () => window.clearTimeout(t);
  }, []);

  // On phones the site header is sticky and sits above this overlay, so the top of the title
  // was hidden under it (Sian, Oct 14). Pad the overlay by the header's height when it's sticky.
  const [topPad, setTopPad] = useState(24);
  useEffect(() => {
    const h = document.querySelector("header");
    if (h && getComputedStyle(h).position === "sticky") setTopPad(Math.ceil(h.getBoundingClientRect().height) + 24);
  }, []);

  const buttonBase = {
    fontFamily: MONO_FONT,
    fontSize: "12px",
    letterSpacing: "0.14em",
    textTransform: "uppercase" as const,
    borderRadius: "999px",
    padding: "14px 30px",
    cursor: "pointer",
    minWidth: "240px",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        zIndex: 40,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // Scrolls (instead of clipping) on short/mobile screens now that the donate panel sits
        // between the title and the exits; the inner wrapper's auto margins keep it centred.
        overflowY: "auto",
        padding: "24px",
        paddingTop: `${topPad}px`,
      }}
    >
      <div style={{ margin: "auto 0", display: "flex", flexDirection: "column", alignItems: "center", gap: "44px", padding: "24px 0" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
        <div
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: "clamp(40px, 6vw, 72px)",
            color: "#f2f2f2",
            opacity: 0,
            animation: "pocFadeUp 1600ms ease 600ms both",
          }}
        >
          Proof of Culture
        </div>
        <div
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "clamp(15px, 1.8vw, 19px)",
            color: "#b5b5b5",
            opacity: 0,
            animation: "pocFadeUp 1600ms ease 900ms both",
            textAlign: "center",
          }}
        >
          Works from a transformative era in Black digital art, 2020–2023
        </div>
        <div
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: "clamp(14px, 1.6vw, 17px)",
            color: "#8f8f8f",
            opacity: 0,
            animation: "pocFadeUp 1600ms ease 1100ms both",
          }}
        >
          Curated by Sian A. Morson
        </div>
      </div>
      {/* Donate panel: fades in with the exits, so the ending still gets its moment of silence first.
          Always in the layout (hidden until its moment) so the title doesn't shift when it appears. */}
      <div
        aria-hidden={!showButtons}
        style={{
          width: "100%",
          maxWidth: "760px",
          borderTop: "1px solid rgba(242,242,242,0.14)",
          borderBottom: "1px solid rgba(242,242,242,0.14)",
          padding: "34px 8px",
          textAlign: "center",
          opacity: 0,
          visibility: showButtons ? "visible" : "hidden",
          pointerEvents: showButtons ? "auto" : "none",
          animation: showButtons ? "pocFadeUp 1400ms ease both" : "none",
        }}
      >
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, fontSize: "clamp(24px, 3vw, 32px)", color: "#f2f2f2", marginBottom: "12px" }}>
          Support the Work of MoDDA
        </div>
        <p style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, fontSize: "clamp(15px, 1.8vw, 19px)", lineHeight: 1.6, color: "#b5b5b5", margin: "0 auto 28px", maxWidth: "620px" }}>
          MoDDA is building a lasting home for the digital art of the Black diaspora. Your contribution supports the preservation of our collection, future exhibitions, and the work of making this cultural history accessible to generations to come.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px 64px" }}>
          <a
            href="https://fundraising.fracturedatlas.org/museum-of-diasporan-digital-art/general_support"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackPoc("Donate Clicked", { option: "Donate (Fractured Atlas)" })}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", padding: "10px 16px", color: "#f2f2f2", textDecoration: "none" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "clamp(18px, 2vw, 21px)", fontWeight: 500 }}>Donate</span>
            <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "clamp(15px, 1.8vw, 17px)", color: "#9a9a9a" }}>Tax-deductible giving</span>
          </a>
          <a
            href="/donate"
            onClick={(e) => {
              e.preventDefault();
              trackPoc("Donate Clicked", { option: "Donate Crypto" });
              navigate("/donate");
            }}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", padding: "10px 16px", color: "#f2f2f2", textDecoration: "none" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
              <rect x="13" y="9" width="8" height="6" rx="1.5" />
              <circle cx="16.5" cy="12" r="0.6" fill="currentColor" />
            </svg>
            <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "clamp(18px, 2vw, 21px)", fontWeight: 500 }}>Donate Crypto</span>
            <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "clamp(15px, 1.8vw, 17px)", color: "#9a9a9a" }}>Support via ETH or USDC</span>
          </a>
        </div>
      </div>
      {/* Always in the layout (hidden until its moment) so the title doesn't shift up when they appear. */}
      <div
        aria-hidden={!showButtons}
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "14px",
          opacity: 0,
          visibility: showButtons ? "visible" : "hidden",
          pointerEvents: showButtons ? "auto" : "none",
          animation: showButtons ? "pocFadeUp 1400ms ease both" : "none",
        }}
      >
        <button
          onClick={onReturn}
          style={{ ...buttonBase, background: "#f2f2f2", color: "#0b0a09", border: "1px solid #f2f2f2" }}
        >
          Return to the timeline ↺
        </button>
        <button
          onClick={() => navigate("/")}
          style={{ ...buttonBase, background: "transparent", color: "#f2f2f2", border: "1px solid rgba(242,242,242,0.5)" }}
        >
          Leave the exhibition →
        </button>
      </div>
      </div>
    </div>
  );
}

// Vercel Web Analytics custom events (script loaded in public/index.html). Page views of
// /proof-of-culture are counted automatically; these add exhibition-level engagement:
// opened, which rooms visitors walk into, and how many reach the closing credits.
function trackPoc(name: string, data?: Record<string, string | number>) {
  try {
    const va = (window as unknown as { va?: (...args: unknown[]) => void }).va;
    if (va) va("event", { name, data });
  } catch (_) {
    // analytics must never break the exhibition
  }
}

export default function ProofOfCultureLine() {
  const [stage, setStage] = useState<Stage>("line");
  const isMobileLine = useIsMobile();
  const [active, setActive] = useState<Milestone | null>(null);
  const [visited, setVisited] = useState<Set<string>>(loadVisited);

  useEffect(() => {
    saveVisited(visited);
  }, [visited]);

  // Finished the exhibition (reached the credits) -- this visit or a saved earlier one.
  const [completed, setCompleted] = useState<boolean>(loadCompleted);

  function startOver() {
    trackPoc("Exhibition Restarted");
    setVisited(new Set());
  }
  const [section, setSection] = useState<SectionState>(null);
  const [infoMilestone, setInfoMilestone] = useState<Milestone | null>(null);
  // True for the brief window between a visitor advancing out of a zero-work section's copy
  // (the closing "Epilogue" today) and that page actually finishing its crossfade to black --
  // see handleAdvance below.
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    trackPoc("Exhibition Opened");
    // Came back on the same device with saved progress from an earlier visit.
    if (visited.size > 0) trackPoc("Returning Visitor", { rooms_visited: visited.size, finished_before: completed ? "yes" : "no" });
  }, []);

  const completedRef = useRef(false);
  useEffect(() => {
    if (stage === "credits" && !completedRef.current) {
      completedRef.current = true;
      trackPoc("Exhibition Completed");
      setCompleted(true);
      saveCompleted();
    }
  }, [stage]);

  // Lock document scroll on every stage except "works" so nothing leaves a residual
  // scrollY behind before the corridor mounts -- otherwise its internal scroll math starts
  // partway through its range and the early slides read as skipped.
  //
  // On "works", the lock is kept a beat longer (~400ms) after mount rather than lifted
  // immediately. Reason: a Mac trackpad's inertial momentum from the gesture that triggered
  // the move into the corridor (a scroll/swipe on the copy page, or the old "Continue"
  // click) keeps delivering scroll input for a bit after it fires -- if the corridor starts
  // listening right away, that leftover momentum carries its position forward past the room
  // title and first work before the visitor ever sees them (confirmed: the reported "3rd
  // eye glo missing" turned out to only be reachable by scrolling back up). Keeping scroll
  // locked briefly lets that momentum bleed off against a document that can't move, so the
  // corridor starts from a genuinely settled position.
  useLayoutEffect(() => {
    if (stage === "works") {
      window.scrollTo(0, 0);
      document.body.style.overflow = "hidden";
      const t = window.setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.style.overflow = "";
      }, 400);
      return () => {
        window.clearTimeout(t);
        document.body.style.overflow = "";
      };
    }
    // The timeline page scrolls (phone: it's a tall vertical list; desktop: on a short window the
    // timeline sits below the fold -- Sian, Oct 14: "you cant scroll down you have to enlarge the
    // window"). Every other stage stays locked.
    document.body.style.overflow = stage === "line" ? "" : "hidden";
    window.scrollTo(0, 0);
    return () => {
      document.body.style.overflow = "";
    };
  }, [stage, isMobileLine]);

  function enter(m: Milestone) {
    trackPoc("Exhibition Room Entered", { room: `${m.year} · ${m.title}` });
    setActive(m);
    setSection(null);
    setStage("copy");
    fetch(`/media/proof-of-culture/${m.id}/meta.json?v=${Date.now()}`, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.json();
      })
      .then((data: SectionData) =>
        setSection({
          data,
          room: withEvents(toRoom(data, m.id, { index: ROOM_MILESTONES.findIndex((x) => x.id === m.id), total: ROOM_MILESTONES.length }), m.id),
        })
      )
      .catch(() => setSection(null));
  }

  const handleAdvance = useCallback(() => {
    if (!section) return;
    // Crossfade the copy page to transparent in place (see CopyPage's `exiting` prop) before
    // actually swapping stages, whether that's into the works corridor or -- for a zero-work
    // section -- into DarkEnding. Previously only the "goes dark" path got this treatment;
    // the works path was still an instant unmount, which read just as abruptly.
    setFadingOut(true);
    window.setTimeout(() => {
      setStage(section.room.items.length === 0 ? "credits" : "works");
      setFadingOut(false);
    }, COPY_EXIT_MS);
  }, [section]);

  function exitToLine() {
    if (active) setVisited((prev) => new Set(prev).add(active.id));
    setStage("line");
    setActive(null);
    setSection(null);
  }

  // Sian: "Need a way to transition between rooms without going back to the timeline" /
  // "Jumps back to timeline before going to the next room" -- the corridor previously only
  // ever offered Exit (back to the timeline). Finds the next explorable milestone after the
  // active one -- skipping any not-yet-`ready` ones and standalone info popups, which aren't a
  // room to walk into -- so MuseumWalkthrough can offer a direct "Next: <title>" alongside Exit.
  // The Dec 2020 - June 2021 constellation is not a stop of its own: its five events are cards
  // inside the rooms they fall in (see EVENTS_IN_ROOMS), so clusters are skipped here too.
  function nextRoomMilestone(fromId: string): Milestone | undefined {
    const idx = MILESTONES.findIndex((x) => x.id === fromId);
    if (idx < 0) return undefined;
    for (let i = idx + 1; i < MILESTONES.length; i++) {
      const m = MILESTONES[i];
      if (m.ready && m.kind !== "info" && m.kind !== "cluster") return m;
    }
    return undefined;
  }

  // Mirror of nextRoomMilestone, walking backward instead -- same skip rules (not-yet-ready
  // milestones, standalone info popups, and the Dec 2020 - June 2021 cluster aren't rooms to
  // walk into).
  function prevRoomMilestone(fromId: string): Milestone | undefined {
    const idx = MILESTONES.findIndex((x) => x.id === fromId);
    if (idx < 0) return undefined;
    for (let i = idx - 1; i >= 0; i--) {
      const m = MILESTONES[i];
      if (m.ready && m.kind !== "info" && m.kind !== "cluster") return m;
    }
    return undefined;
  }

  function goToNext(next: Milestone) {
    if (active) setVisited((prev) => new Set(prev).add(active.id));
    enter(next);
  }

  function goToPrev(prev: Milestone) {
    if (active) setVisited((prev2) => new Set(prev2).add(active.id));
    enter(prev);
  }

  // Keep the page itself dark for as long as the exhibition is mounted, so that any gap between
  // one stage's layers unmounting and the next one's painting can only ever show near-black --
  // never the site's white body (the "small flash between transitions").
  useLayoutEffect(() => {
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = GROUND;
    document.documentElement.style.background = GROUND;
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, []);

  // Permanent near-black backdrop, present in EVERY stage (the corridor and dark ending used
  // to return without it, so it unmounted and remounted across each section change). z-index 0
  // sits above the site's in-flow Header/Footer but under the corridor's own layer (z 1) and
  // the copy page (z 40); on the timeline it stays behind the site header, as before.
  const backdrop = <div style={{ position: "fixed", inset: 0, background: GROUND, zIndex: stage === "line" ? -1 : 0 }} />;

  if (stage === "works" && section) {
    const next = active ? nextRoomMilestone(active.id) : undefined;
    const prev = active ? prevRoomMilestone(active.id) : undefined;
    return (
      <>
        {backdrop}
        <MuseumWalkthrough
          rooms={[section.room]}
          onExit={exitToLine}
          onNext={next ? () => goToNext(next) : undefined}
          nextLabel={next?.title || next?.year}
          onPrev={prev ? () => goToPrev(prev) : undefined}
          prevLabel={prev?.title || prev?.year}
          scrollUnitFactor={1.2}
          softEntry
        />
      </>
    );
  }

  if (stage === "credits") {
    return (
      <>
        {backdrop}
        <ArtistCredits onDone={() => setStage("dark")} />
      </>
    );
  }

  if (stage === "dark") {
    return (
      <>
        {backdrop}
        <DarkEnding onReturn={exitToLine} />
      </>
    );
  }

  return (
    <>
      {backdrop}
      {stage === "line" && <CultureLine visited={visited} onEnter={enter} onInfo={setInfoMilestone} onStartOver={startOver} completed={completed} />}
      {stage === "copy" && active && (
        <CopyPage m={active} section={section} onAdvance={handleAdvance} exiting={fadingOut} />
      )}
      {infoMilestone && <MilestoneInfoPopup m={infoMilestone} onClose={() => setInfoMilestone(null)} />}
    </>
  );
}
